import type {
  LayoutSection,
  LocalizedLabel,
  ProductOffer,
} from '@segmently/schema-models/v2';
import { resolveLabel, type TemplateVariables } from '../../../utils/contentResolver';

export interface LinkedProductRenderResult {
  section: LayoutSection;
  purchaseProductId?: string;
  variables?: TemplateVariables;
}

function isProductCatalogSection(section: LayoutSection | undefined): boolean {
  return section?.kind === 'ProductCatalog' && Boolean(section.productCatalogContent?.items?.length);
}

function findProductCatalogSection(
  sections: LayoutSection[],
  sectionId: string | undefined,
): LayoutSection | undefined {
  if (!sectionId) return undefined;

  for (const section of sections) {
    if (section.id === sectionId && isProductCatalogSection(section)) {
      return section;
    }

    if (section.kind === 'CustomEmbed') {
      const childSections = section.embedContent?.childSections ?? [];
      const match = childSections.find((child) => child.id === sectionId && isProductCatalogSection(child));
      if (match) return match;
    }
  }

  return undefined;
}

function resolveSelectedProduct(
  catalogSection: LayoutSection | undefined,
  selectedProductIndexMap?: Map<string, number>,
): ProductOffer | undefined {
  const products = catalogSection?.productCatalogContent?.items ?? [];
  if (products.length === 0 || !catalogSection) return undefined;

  const selectedIndex = selectedProductIndexMap?.get(catalogSection.id);
  if (selectedIndex != null && selectedIndex >= 0 && selectedIndex < products.length) {
    return products[selectedIndex];
  }

  const defaultIndex = products.findIndex((item) => item.isSelected);
  return defaultIndex >= 0 ? products[defaultIndex] : products[0];
}

export function isPurchaseButton(section: LayoutSection): boolean {
  return section.kind === 'Button' && String(section.buttonContent?.action?.kind ?? '').toLowerCase() === 'purchase';
}

function mergeProductVariables(
  variables: TemplateVariables | undefined,
  product: ProductOffer | undefined,
  itemVariablesMap?: Record<string, TemplateVariables>,
): TemplateVariables | undefined {
  const productVariables = product?.productId ? itemVariablesMap?.[product.productId] : undefined;
  return productVariables ? { ...variables, ...productVariables } : variables;
}

function labelWithFallbackContent(
  currentLabel: LocalizedLabel | undefined,
  fallbackLabel: LocalizedLabel,
): LocalizedLabel {
  return {
    ...fallbackLabel,
    ...(currentLabel ?? {}),
    translations: fallbackLabel.translations,
    templateParams: fallbackLabel.templateParams,
    kind: fallbackLabel.kind,
  } as LocalizedLabel;
}

function applyPurchaseButtonFallback(
  section: LayoutSection,
  product: ProductOffer | undefined,
  locale: string,
): LayoutSection {
  const buttonContent = section.kind === 'Button' ? section.buttonContent : undefined;
  if (!isPurchaseButton(section) || !buttonContent || buttonContent.kind === 'icon' || !product?.purchaseLabel) {
    return section;
  }

  const labelContent = buttonContent.content as LocalizedLabel | undefined;
  if (resolveLabel(labelContent, locale)) {
    return section;
  }

  return {
    ...section,
    buttonContent: {
      ...buttonContent,
      content: labelWithFallbackContent(labelContent, product.purchaseLabel),
    },
  };
}

function applyTextDescriptionFallback(
  section: LayoutSection,
  product: ProductOffer | undefined,
  locale: string,
): LayoutSection {
  if (section.kind !== 'Text' || !product?.descriptionLabel) {
    return section;
  }

  if (resolveLabel(section.textContent?.title, locale)) {
    return section;
  }

  return {
    ...section,
    textContent: {
      ...(section.textContent ?? {}),
      title: labelWithFallbackContent(section.textContent?.title, product.descriptionLabel),
    },
  };
}

export function buildLinkedProductRenderResult({
  section,
  sections,
  selectedProductIndexMap,
  itemVariablesMap,
  variables,
  locale,
}: {
  section: LayoutSection;
  sections: LayoutSection[];
  selectedProductIndexMap?: Map<string, number>;
  itemVariablesMap?: Record<string, TemplateVariables>;
  variables?: TemplateVariables;
  locale: string;
}): LinkedProductRenderResult {
  const catalogSection = findProductCatalogSection(sections, section.parentSectionId);
  const selectedProduct = resolveSelectedProduct(catalogSection, selectedProductIndexMap);
  const sectionWithPurchaseFallback = applyPurchaseButtonFallback(section, selectedProduct, locale);
  const sectionWithTextFallback = applyTextDescriptionFallback(sectionWithPurchaseFallback, selectedProduct, locale);

  return {
    section: sectionWithTextFallback,
    purchaseProductId: resolveSelectedProduct(catalogSection ?? (!section.parentSectionId
      ? sections.find(candidate => candidate.kind === 'ProductCatalog') : undefined), selectedProductIndexMap)?.productId,
    variables: mergeProductVariables(variables, selectedProduct, itemVariablesMap),
  };
}
